import type { Node as ProsemirrorNode, Schema } from 'prosemirror-model';
import { DOMSerializer } from 'prosemirror-model';
import type { Transaction } from 'prosemirror-state';
import { NodeSelection, TextSelection } from 'prosemirror-state';
import type { NodeWithPos } from '@tiptap/vue-2';
import { findChildren } from '@tiptap/vue-2';
import type { Field, TextContentDoc } from '../types';
import { NodeAlign, ProsemirrorType } from '../types/enums';
import type { Text, MergeTagNode } from '../types/text-content';
import { EditorHelper } from './editor.helper';

export class ProsemirrorHelper {
  static selectNode(transaction: Transaction, position: number): Transaction {
    return transaction.setSelection(NodeSelection.create(transaction.doc, position));
  }

  static selectTextNode(transaction: Transaction, position: number): Transaction {
    return transaction.setSelection(TextSelection.create(transaction.doc, position));
  }

  static replaceBrNodes(fromTransaction: Transaction, schema: Schema): Transaction {
    let transaction = fromTransaction;

    const brNodes: NodeWithPos[] = findChildren(
      transaction.doc,
      (node: ProsemirrorNode) => node.type.name === ProsemirrorType.HardBreak
    ).reverse();

    brNodes.forEach(({ node, pos }: NodeWithPos) => {
      transaction = transaction.replaceWith(
        pos,
        pos + node.nodeSize,
        schema.text('\n', node.marks)
      );
    });

    return transaction;
  }

  static getStringFromTransaction(transaction: Transaction, schema: Schema): string {
    // doc.content.firstChild.content - targets content of the root paragraph element
    const fragment = DOMSerializer.fromSchema(schema).serializeFragment(
      transaction.doc.content.firstChild.content
    );
    const container = document.createElement('div');
    container.appendChild(fragment);
    return container.innerText;
  }

  private static makeMergeTagNode(mergeTag: string, fields: Field[]): MergeTagNode {
    const tagId = mergeTag.substr(2, EditorHelper.NANO_ID_LENGTH);

    const { category, mergeKey, customName } = fields.find(field => field.id === tagId) || {
      category: null,
      customName: null,
      mergeKey: null,
    };

    return {
      type: ProsemirrorType.MergeTag,
      attrs: {
        category,
        customName,
        mergeKey,
        tagId,
      },
    };
  }

  private static makeTextNode(text: string): Text {
    return {
      type: ProsemirrorType.Text,
      text: text.replace(/(\n)+/g, '<br>'),
    };
  }

  static getInputContent(value: string, fields: Field[]): TextContentDoc | string {
    if (!value) {
      return '';
    }

    const mergeTagRegex = new RegExp(EditorHelper.mergeTagExpression);
    const newline = '\n';
    const tagOrNewlineExpression = new RegExp(`(${mergeTagRegex.source}|${newline})`);

    return {
      type: ProsemirrorType.Doc,
      content: [
        {
          attrs: { align: NodeAlign.Left },
          type: ProsemirrorType.Paragraph,
          content: value
            .split(tagOrNewlineExpression)
            .filter(node => node)
            .map(node => {
              if (mergeTagRegex.test(node)) {
                return ProsemirrorHelper.makeMergeTagNode(node, fields);
              }

              if (node === newline) {
                return { type: ProsemirrorType.HardBreak };
              }

              return ProsemirrorHelper.makeTextNode(node);
            }),
        },
      ],
    };
  }

  static getSafeText(text: null | string): string {
    return text ? JSON.parse(text) : '';
  }
}
